import type {UseQueryOptions} from '@tanstack/react-query'
import {exhaustive} from '@freckle/exhaustive'
import {mthen} from '@freckle/maybe'
import {useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {
  isElaPremium,
  isMathPremium,
} from '@freckle/educator-entities/ts/common/logic/premium-access'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {fancyOperations} from '@freckle/educator-entities/ts/openapi-clients'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {schoolHasStarIntegration} from '@freckle/educator-entities/ts/roster/models/school'
import {useTeacher} from './use-teacher'

// Determine if it's even possible that we synced placements
export const placementsMayExist = (
  contentArea: string,
  schools: SchoolAttrs[],
  premiumLicenses: PremiumLicensesAttrs,
): boolean =>
  schools.some(schoolHasStarIntegration) &&
  ((contentArea === 'math' && isMathPremium(premiumLicenses)) ||
    (contentArea === 'ela' && isElaPremium(premiumLicenses)))

// Determine if any students have star placements for a ContentArea
export const useHasStarPlacements = (
  contentArea: 'math' | 'ela',
  options?: Partial<UseQueryOptions<boolean>>,
): boolean => {
  const teacher = useTeacher()

  const result = useAsyncOnceDefined(
    ({teacher}) => fetchHasStarPlacements({teacherId: teacher.id, contentArea}),
    'fetchHasStarPlacements ',
    [mthen(teacher.data, teacher => teacher.id), contentArea],
    {teacher},
    // Placements sync infrequently
    {staleTime: 60 * 60 * 1000, ...options},
  )

  return result.data === true
}

type ExpandField = NonNullable<
  NonNullable<
    fancyOperations['getTeacherStudentsPlacementSummariesR']['parameters']['query']
  >['expand']
>[0]

const mkExpand = (contentArea: 'math' | 'ela'): ExpandField => {
  switch (contentArea) {
    case 'math':
      return 'hasStarMathPlacements'
    case 'ela':
      return 'hasStarReadingPlacements'
    default:
      return exhaustive(contentArea)
  }
}

const fetchHasStarPlacements = ({
  teacherId,
  contentArea,
}: {
  teacherId: number
  contentArea: 'math' | 'ela'
}): Promise<boolean> =>
  fancyClient
    .GET(
      '/2/teachers/{TeacherStudentsPlacementSummariesR-TeacherId}/students/placement-summaries',
      {
        params: {
          path: {'TeacherStudentsPlacementSummariesR-TeacherId': teacherId},
          query: {expand: [mkExpand(contentArea)]},
        },
      },
    )
    .then(extractData)
    .then(data => data[mkExpand(contentArea)] === true)
